import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  from,
  map,
  of,
  switchMap
} from 'rxjs';
import {
  SSO_SAVE_FAILED,
  ssoApiErrorMessage
} from 'src/app/admin/admin-admin/sso-connections/sso-connections';
import { companiesService } from 'src/app/shared/vive-api/companies.service';
import { ssoConnectionsService } from 'src/app/shared/vive-api/sso-connections.service';
import {
  CompanyDto,
  SsoConnectionsCreateRequestDtoProtocolEnum,
  SsoConnectionsCreateRequestDtoScopeEnum
} from 'src/app/shared/vive-api/vive-api-models';

const CLIENT_SEARCH_DEBOUNCE_MS = 300;
const CLIENT_SEARCH_MIN_LENGTH = 2;
const CLIENT_SEARCH_RESULTS = 10;

// Creates a draft only. Protocol, scope and client cannot change afterwards, so
// they are asked here; everything else is filled in on the connection's page.
@Component({
  selector: 'app-add-sso-connection-modal',
  templateUrl: './add-sso-connection-modal.component.html',
  styleUrl: './add-sso-connection-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddSsoConnectionModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly clientQueries = new Subject<string>();

  public name = '';
  public protocol: SsoConnectionsCreateRequestDtoProtocolEnum = 'oidc';
  public scope: SsoConnectionsCreateRequestDtoScopeEnum = 'client';
  public clientQuery = '';
  public clientResults: CompanyDto[] = [];
  public searchedClients = false;
  public selectedClient: CompanyDto | null = null;
  public saving = false;
  public error: string | null = null;

  public constructor() {
    this.clientQueries
      .pipe(
        debounceTime(CLIENT_SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        // null means "no search ran", so a failed search shows nothing rather
        // than claiming no client matches.
        switchMap((query) =>
          query.length < CLIENT_SEARCH_MIN_LENGTH
            ? of(null)
            : from(
                companiesService.findAll({
                  queryParams: {
                    legalName: query,
                    sortBy: 'legalName',
                    pageSize: CLIENT_SEARCH_RESULTS
                  }
                })
              ).pipe(
                map(({ data }) => data),
                catchError(() => of(null))
              )
        ),
        takeUntilDestroyed(inject(DestroyRef))
      )
      .subscribe((results) => {
        this.clientResults = results ?? [];
        this.searchedClients = results !== null;
        this.cdr.markForCheck();
      });
  }

  public get canCreate(): boolean {
    return (
      !this.saving &&
      this.name.trim() !== '' &&
      (this.scope === 'staff' || this.selectedClient !== null)
    );
  }

  public clientName(company: CompanyDto): string {
    return company.legalName ?? `Client ${company.id}`;
  }

  public searchClients(query: string): void {
    this.clientQuery = query;
    this.selectedClient = null;
    this.clientQueries.next(query.trim());
  }

  public chooseClient(company: CompanyDto): void {
    this.selectedClient = company;
    this.clientQuery = this.clientName(company);
    this.clientResults = [];
    this.searchedClients = false;
  }

  public cancel(): void {
    this.activeModal.dismiss();
  }

  public async create(): Promise<void> {
    if (!this.canCreate) {
      return;
    }

    this.saving = true;
    this.error = null;
    this.cdr.markForCheck();

    try {
      const created = await ssoConnectionsService.create({
        data: {
          name: this.name.trim(),
          protocol: this.protocol,
          scope: this.scope,
          ...(this.scope === 'client' && this.selectedClient !== null
            ? { CompanyId: this.selectedClient.id }
            : {})
        }
      });
      this.activeModal.close(created);
    } catch (error) {
      this.error = ssoApiErrorMessage(error, SSO_SAVE_FAILED);
      this.saving = false;
      this.cdr.markForCheck();
    }
  }
}
